import { yupResolver } from "@hookform/resolvers/yup";
import { Autocomplete, Box } from "@mui/material";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { object, string } from "yup";

import { useProject } from "../../../contexts/project.context";
import { useClients } from "../../../queries/hooks";
import { Requesting } from "../../../types";
import { Client } from "../../../types/client.type";
import { Input } from "../../Input";
import RequesterInfo from "../components/RequesterInfo";
import TabContent from "../components/TabContent";

export interface ProjectClientHandle {
  validate: () => boolean;
}

interface ProjectClientProps {
  onNext: () => void;
}

const emptyRequesting = { _id: "", name: "", email: "" };
const emptyClient = { _id: "", name: "", email: "", segment: "", tax_id: "" };

const schema = object({
  client: object({
    _id: string(),
    name: string(),
    email: string(),
    segment: string(),
    tax_id: string(),
  }).required("Cliente é obrigatório"),
});

const ProjectClient = forwardRef<ProjectClientHandle, ProjectClientProps>(
  ({ onNext }: ProjectClientProps, ref) => {
  const { client, updateClient, project } = useProject();
  const [hasRequester, setHasRequester] = useState(false);
  const [requestings, setRequestings] = useState<Requesting[]>([
    emptyRequesting,
  ]);
  const [clientError, setClientError] = useState(false);
  const [requesterError, setRequesterError] = useState(false);

  const updateRequestings = (newRequesting: any) => {
    setRequestings([newRequesting]);
    if (newRequesting?._id) setRequesterError(false);
  };

  const {
    handleSubmit,
    setValue,
    reset,
    watch,
    clearErrors,
  } = useForm({
    defaultValues: {
      client: emptyClient as Client,
    },
    resolver: yupResolver(schema),
    mode: "all",
  });

  const clientWatch = watch("client");
  const { data: clientsData, isLoading: clientLoading } = useClients();

  useImperativeHandle(ref, () => ({
    validate: () => {
      const missingClient = !clientWatch?._id || clientWatch._id.length < 1;
      const hasRequesterFromRequestings =
        requestings && requestings.length > 0 && !!requestings?.[0]?._id;
      const missingRequester = !hasRequesterFromRequestings && !project?.requester?._id;
      if (missingClient) {
        setClientError(true);
        toast.error("Cliente é obrigatório.");
      }
      if (missingRequester) {
        setRequesterError(true);
        toast.error("Solicitante é obrigatório.");
      }
      return !missingClient && !missingRequester;
    },
  }));

  function onSubmit(form: any) {
    const missingClient = !clientWatch?._id || clientWatch._id.length < 1;
    const hasRequesterFromRequestings =
      requestings && requestings.length > 0 && !!requestings?.[0]?._id;
    const missingRequester = !hasRequesterFromRequestings && !project?.requester?._id;

    if (missingClient) {
      setClientError(true);
      toast.error("Cliente é obrigatório.");
    }
    if (missingRequester) {
      setRequesterError(true);
      toast.error("Solicitante é obrigatório.");
    }
    if (missingClient || missingRequester) return;

    updateClient({
      clientSelected: form.client,
      requestings: requestings.filter((requesting) => requesting._id),
    });
    onNext();
  }

  useEffect(() => {
    if (client) {
      reset({ client: client.clientSelected });
      if (client.requestings && client.requestings.length > 0) {
        setRequestings([...client.requestings]);
      } else if (project?.requester?._id) {
        setRequestings([
          {
            _id: project.requester._id,
            name: project.requester.name,
            email: project.requester.email,
          },
        ]);
      }
    }
  }, [client]);

  return (
    <TabContent
      onSubmit={handleSubmit(onSubmit, (invalidErrors) => {
        for (const value of Object.values(invalidErrors)) {
          if (value?.message) toast.error(value.message);
        }
      })}
    >
      <Autocomplete
        options={clientsData || []}
        getOptionLabel={(option) => {
          if (option.name === "" && !!option._id)
            return "Cliente sem nome. Atualize na área de clientes";
          if (option.nickname) return option.nickname;
          return "";
        }}
        filterOptions={(options, state) => {
          const inputValue = state.inputValue.toLowerCase();
          return options.filter((option) => {
            const fullLabel = `${option.name}/${option.nickname} - ${option.tax_id}`.toLowerCase();
            return fullLabel.includes(inputValue);
          });
        }}
        renderOption={(props, option) => {
          let label = "";
          if (option.name !== "")
            label = `${option.name} / ${option.nickname} – ${option.tax_id}`;
          if (option.name === "" && !!option._id)
            label = "Cliente sem nome. Atualize na área de clientes";
          return (
            <li {...props} key={option._id}>
              {label}
            </li>
          );
        }}
        value={clientWatch}
        isOptionEqualToValue={(option, value) => option._id === value._id}
        onChange={(_, client) => {
          if (client === null) {
            setValue("client", emptyClient);
            clearErrors("client");
            setClientError(true);
            return;
          }
          setValue("client", client);
          clearErrors("client");
          setClientError(false);
        }}
        loading={clientLoading}
        loadingText={"Carregando..."}
        renderInput={(params) => (
          <Input
            label="Cliente cadastrado *"
            placeholder="Clique para selecionar"
            error={clientError}
            helperText={clientError ? "Campo Obrigatório" : undefined}
            {...params}
          />
        )}
      />
      <Box display="flex" alignItems="center" gap={2}>
        <Input
          label="CPF/CNPJ"
          value={clientWatch?.tax_id}
          disabled
          placeholder="00.000.000/0000-00"
        />
        <Input
          label="Segmento"
          value={clientWatch?.segment}
          disabled
          placeholder="Indefinido"
        />
      </Box>
      <RequesterInfo
        client={clientWatch?._id}
        requester={
          project?.requester
            ? project?.requester
            : requestings?.[0] || {
                name: "",
                email: "",
                phone: "",
              }
        }
        onUpdateRequestings={updateRequestings}
        hasError={requesterError}
      />
    </TabContent>
    );
  }
);

export default ProjectClient;
